import { useCallback, useMemo, useRef } from "react"

import { useGeoServerRetrieveFeature, getFeatureCenterPoint } from "@l2r-front/l2r-geodata"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"

import { ImageTypeSwitch } from "../../components/ImageTypeSwitch"
import { IMAGE_COLLECTION_TYPE_PHOTO, IMAGE_COLLECTION_TYPE_PANO } from "../../constants/imageCollectionsTypes"
import { useImageCollectionStateContext, useImageCollectionDispatchContext } from "../../contexts/ImageCollectionContext"
import { useDownloadNearbyImages } from "../../hooks/queries/imageCollection/useNearestImageInCollection"

import { ImageNavigator } from "../ImageNavigator"
import { PanoramicViewer } from "../PanoramicViewer/PanoramicViewer"
import { PhotoViewer } from "../PhotoViewer"
import { StreetView } from "../StreetView"

import * as Styled from "./ImageViewer.styled"

export const ImageViewer = (props) => {

    const {
        className,
        bottomRightComponent,
        topRightComponent,
    } = props

    const { imageFeatureClickedPosition, imageCollection, isLoadingImageCollections, triggeringFeatureId, selectedImageCollectionType } = useImageCollectionStateContext()
    const { setSelectedImageCollectionType, isImageCollectionAvailable } = useImageCollectionDispatchContext()
    const isMobile = useIsMobileDevice()
    const {
        data: triggeringFeature,
        isLoading: isLoadingTriggeringFeature,
    } = useGeoServerRetrieveFeature(triggeringFeatureId)

    const initialPosition = useMemo(() => {
        if (imageFeatureClickedPosition) {
            return imageFeatureClickedPosition
        } else if (triggeringFeature) {
            const featureCenter = getFeatureCenterPoint(triggeringFeature)
            return featureCenter.geometry.coordinates
        }

        return null
    }, [imageFeatureClickedPosition, triggeringFeature])

    useDownloadNearbyImages(initialPosition)

    const visitedViewersRef = useRef(new Set())
    const isStreetViewActive = selectedImageCollectionType !== IMAGE_COLLECTION_TYPE_PHOTO && selectedImageCollectionType !== IMAGE_COLLECTION_TYPE_PANO
    const isPanoActive = selectedImageCollectionType === IMAGE_COLLECTION_TYPE_PANO
    if (isStreetViewActive) {
        visitedViewersRef.current.add("streetView")
    }
    if (isPanoActive) {
        visitedViewersRef.current.add("pano")
    }

    const onTypeChange = useCallback((type) => {
        setSelectedImageCollectionType(type)
    }, [setSelectedImageCollectionType])

    return (
        <Styled.ImageViewerContainer className={className}>
            <Styled.Container>
                {selectedImageCollectionType === IMAGE_COLLECTION_TYPE_PHOTO &&
                    <PhotoViewer initialPosition={initialPosition} date={imageCollection?.date} />
                }
                {visitedViewersRef.current.has("pano") &&
                    <Styled.ViewerLayer isActive={isPanoActive}>
                        <PanoramicViewer active={isPanoActive} initialPosition={initialPosition} date={imageCollection?.date} />
                    </Styled.ViewerLayer>
                }
                {visitedViewersRef.current.has("streetView") &&
                    <Styled.ViewerLayer isActive={isStreetViewActive}>
                        <StreetView active={isStreetViewActive} initialPosition={initialPosition} />
                    </Styled.ViewerLayer>
                }
                {<Styled.TopRightContainer>
                    {topRightComponent}
                    {isImageCollectionAvailable() && <ImageTypeSwitch
                        id="image-type-switch"
                        currentImageCollectionType={selectedImageCollectionType}
                        onChange={onTypeChange}
                        imageCollectionType={imageCollection.type}
                    />}
                </Styled.TopRightContainer>
                }
                {bottomRightComponent && <Styled.BottomRightContainer isMobile={isMobile}>
                    {bottomRightComponent}
                </Styled.BottomRightContainer>}
                <Styled.BottomCenterContainer isMobile={isMobile}>
                    {
                        (selectedImageCollectionType === IMAGE_COLLECTION_TYPE_PHOTO ||
                            selectedImageCollectionType === IMAGE_COLLECTION_TYPE_PANO) &&
                        <ImageNavigator />
                    }
                </Styled.BottomCenterContainer>
            </Styled.Container>
            {
                (isLoadingImageCollections || isLoadingTriggeringFeature) &&
                <Styled.LoadingContainer>
                    <Styled.Skeleton />
                </Styled.LoadingContainer>
            }
        </Styled.ImageViewerContainer >
    )
}

ImageViewer.propType = {
    bottomRightComponent: PropTypes.node,
    topLeftComponent: PropTypes.node,
}